import { Component, forwardRef, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { MessageType } from '@constants/message/1.0';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { LoadingColor } from '@constants/loading/1.0';
import { FormCanDeactivate } from '@extensions/form-can-deactivate';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { Tenant } from '@generated/tcea/models/tenant';
import { NgIf, NgFor } from '@angular/common';
import { MatFormField, MatError } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { NumbersOnlyDirective } from '../../directives/numbers-only/1.0/numbers-only.directive';
import { MatDivider } from '@angular/material/divider';

@Component({
  selector: 'txp-order-limits-template-form',
  templateUrl: './order-limits-template-form.component.html',
  styleUrls: ['./order-limits-template-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OrderLimitsTemplateFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OrderLimitsTemplateFormComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatInput,
    NumbersOnlyDirective,
    MatError,
    NgFor,
    MatDivider,
    TranslateModule,
  ],
})
export class OrderLimitsTemplateFormComponent
  extends FormCanDeactivate
  implements ControlValueAccessor, Validator, OnInit, OnChanges
{
  @Input() brand: Tenant;
  @Input() hasDefault: boolean;

  buttonSize = ButtonSize;
  buttonStyle = ButtonStyle;
  messageType = MessageType;
  loadingColor = LoadingColor;

  form: UntypedFormGroup;
  brandId: string;
  orderChargeLimitsReadOnly = true;
  createTemplateError = false;
  updateTemplateError = false;
  onChangeFn: any;
  onTouched: any;
  isSaving = false;
  showLoadError = false;
  templateId: string;
  loadTemplateError = false;
  dispositionKeys: string[];

  private unsubscribe = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private permissionsService: PermissionsService,
    private translateService: TranslateService,
    private route: ActivatedRoute,
    private router: Router,
  ) {
    super();
    this.orderChargeLimitsReadOnly = !this.permissionsService.getPermission(
      'ordering-templates:write',
    );
    this.form = this.formBuilder.group({
      serviceTypes: new UntypedFormGroup({}),
    });
    this.orderChargeLimitsReadOnly ? this.form.disable() : this.form.enable();
  }

  ngOnInit() {
    if (this.hasDefault) {
      this.form.addControl(
        'minimum',
        new UntypedFormControl({ value: '', disabled: this.orderChargeLimitsReadOnly }),
      );
      this.form.addControl(
        'maximum',
        new UntypedFormControl({ value: '', disabled: this.orderChargeLimitsReadOnly }),
      );
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.brand) {
      this.brandId = changes.brand.currentValue?.id;
      this.dispositionKeys = changes.brand.currentValue?.supportedServiceTypes
        ? JSON.parse(JSON.stringify(changes.brand.currentValue?.supportedServiceTypes))
        : [];
      this.setupServiceTypesForm();
    }
  }

  registerOnChange(fn: any): void {
    this.onChangeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((data) => {
      const newTemplate = {
        serviceTypes: Object.keys(data?.serviceTypes).map((key) => {
          const item = data.serviceTypes[key];
          const obj: any = {
            serviceType: key,
          };

          if (typeof item.minimum === 'number') {
            obj.minimum = item.minimum;
          }

          if (typeof item.maximum === 'number') {
            obj.maximum = item.maximum;
          }

          return obj;
        }),
      };

      if (typeof data.minimum === 'number') {
        Object.assign(newTemplate, {
          minimum: data.minimum,
        });
      }

      if (typeof data.maximum === 'number') {
        Object.assign(newTemplate, {
          maximum: data.maximum,
        });
      }

      fn(newTemplate);
    });
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  writeValue(obj: any) {
    if (!obj) {
      return;
    }

    this.form.reset(obj);

    if (Array.isArray(obj?.serviceTypes)) {
      obj.serviceTypes.forEach((item) => {
        this.form.get(`serviceTypes.${item.serviceType}`)?.patchValue(item);
      });
    }

    this.form.markAsPristine();
  }

  validate(c: AbstractControl): ValidationErrors | null {
    return this.form.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.fieldsAreInvalid'),
          },
        };
  }

  setupServiceTypesForm() {
    const len = this.dispositionKeys?.length;
    const serviceTypesControl = this.form.get('serviceTypes') as UntypedFormGroup;

    for (let i = 0; i < len; i++) {
      const group = new UntypedFormGroup({
        minimum: new UntypedFormControl({ value: '', disabled: this.orderChargeLimitsReadOnly }),
        maximum: new UntypedFormControl({ value: '', disabled: this.orderChargeLimitsReadOnly }),
      });

      serviceTypesControl.addControl(this.dispositionKeys[i], group);
    }
  }

  reset() {
    this.createTemplateError = false;
    this.updateTemplateError = false;
    this.loadTemplateError = false;
    this.isSaving = false;
  }

  onBack() {
    this.router.navigate(['../'], { relativeTo: this.route });
  }

  onCancel() {
    this.form.reset();
    this.form.markAsPristine();
    this.onBack();
  }
}
